import type { ComponentType } from "react";

import type { SettingsConfigKey } from "./settingsForm";
import {
  BrandingSection,
  CurrencySection,
  DealCategoriesSection,
  DealStagesSection,
  InboundEmailSection,
  NoteStatusesSection,
  PipelineStatusesSection,
  RecordTypesSection,
  TaskTypesSection,
} from "./SettingsSections";
import { ChannelsSection } from "./ChannelsSection";
import { TagsSection } from "./TagsSection";
import { WhitelistSection } from "./WhitelistSection";

export type SettingsSectionId =
  | "branding"
  | "whitelist"
  | "inbound_email"
  | "record_types"
  | "note_statuses"
  | "note_channels"
  | "tags"
  | "currency"
  | "deal_stages"
  | "pipeline_statuses"
  | "deal_categories"
  | "task_types";

type SettingsSection = {
  label: string;
  fallback: string;
  Component: ComponentType;
  /**
   * The configuration keys the section's inputs edit. A section without any
   * (tags, whitelist, inbound email) is not part of the settings form: it
   * saves each change as it is made, or has nothing to save.
   */
  configKeys?: readonly SettingsConfigKey[];
};

/**
 * Every section of Settings, by the id the page scrolls to. The page lays
 * them out in groups; a settings icon elsewhere in the app opens one of them
 * in a dialog (SettingsShortcut). LCCRM-86.
 */
export const SETTINGS_SECTIONS: Record<SettingsSectionId, SettingsSection> = {
  branding: {
    label: "crm.settings.sections.branding",
    fallback: "Branding",
    Component: BrandingSection,
    configKeys: ["title", "lightModeLogo", "darkModeLogo"],
  },
  whitelist: {
    label: "crm.settings.sections.whitelist",
    fallback: "Signup Whitelist",
    Component: WhitelistSection,
  },
  // Keeps the crm.profile.inbound.* keys it was given when this card lived on
  // the profile page. SettingsPageMobile reads the same keys, so renaming them
  // would mean touching both message catalogs and the mobile page for no gain.
  inbound_email: {
    label: "crm.profile.inbound.title",
    fallback: "Inbound email",
    Component: InboundEmailSection,
  },
  record_types: {
    label: "crm.settings.sections.record_types",
    fallback: "Record Types",
    Component: RecordTypesSection,
    configKeys: ["recordTypes"],
  },
  note_statuses: {
    label: "crm.settings.sections.note_statuses",
    fallback: "Note Statuses",
    Component: NoteStatusesSection,
    configKeys: ["noteStatuses"],
  },
  // A table of its own, like tags: no configKeys, each change saved as made.
  note_channels: {
    label: "crm.settings.sections.note_channels",
    fallback: "Note Channels",
    Component: ChannelsSection,
  },
  tags: {
    label: "crm.settings.sections.tags",
    fallback: "Tags",
    Component: TagsSection,
  },
  currency: {
    label: "crm.settings.deals.currency",
    fallback: "Currency",
    Component: CurrencySection,
    configKeys: ["currency"],
  },
  deal_stages: {
    label: "crm.settings.deals.stages",
    fallback: "Stages",
    Component: DealStagesSection,
    configKeys: ["dealStages"],
  },
  pipeline_statuses: {
    label: "crm.settings.deals.pipeline_statuses",
    fallback: "Pipeline Statuses",
    Component: PipelineStatusesSection,
    configKeys: ["dealPipelineStatuses"],
  },
  deal_categories: {
    label: "crm.settings.deals.categories",
    fallback: "Categories",
    Component: DealCategoriesSection,
    configKeys: ["dealCategories"],
  },
  task_types: {
    label: "crm.settings.tasks.types",
    fallback: "Types",
    Component: TaskTypesSection,
    configKeys: ["taskTypes"],
  },
};
